/**
 * A-16 — every payment and refund, to track them and to file returns. A period (this month by
 * default; quarters, financial years or custom dates), type and status filters and a search
 * (order, patient, phone or Razorpay ID); the period's totals — money in and out, and the GST on
 * the orders paid in it; a server-paged table (a row opens its order); and the downloads: the
 * register as CSV, Excel (with GST-by-rate, HSN and documents sheets) or PDF, GSTR-1 JSON for the
 * GST offline tool, and Tally XML vouchers. The view lives in the URL (?p=&from=&to=&type=&status=
 * &q=&sort=&dir=&per=&order=).
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { downloadTransactionReport, listTransactions, transactionsSummary } from "../../api/adminOrders";
import type { ReportFormat, TallyLedgerNames, Transaction, TransactionQuery, TransactionStatus, TransactionType } from "../../api/adminOrders";
import { describeError } from "../../api/errors";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  FilterField,
  SearchBox,
  SelectFilter,
  SortFilter,
} from "../../components/ui/FilterBar";
import { Modal } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { formatINR } from "../../lib/money";
import { gstr1Period, PERIOD_PRESETS, presetRange, rangeLabel } from "../../lib/periods";
import type { DayRange, PeriodPreset } from "../../lib/periods";
import { phoneLabel } from "../../lib/phone";
import type { TableColumn } from "../../lib/tableColumns";
import { useSearchBox, useUrlParams } from "../../lib/urlState";
import { AdminOrderDetail, StatusPill } from "./AdminOrderDetail";

const TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: "", label: "All" },
  { value: "payment", label: "Payments" },
  { value: "refund", label: "Refunds" },
];
// "any" in the URL is every status; no value is the default, completed.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed or expired" },
  { value: "any", label: "All" },
];
const SORTS: { value: "at" | "amount"; label: string }[] = [
  { value: "at", label: "Date" },
  { value: "amount", label: "Amount" },
];

// What it is, and the amount, at every width; status from sm, patient from md, the Razorpay ID
// from lg (narrower screens show them in the first cell).
const COLUMNS: TableColumn[] = [
  { key: "tx", header: "Transaction", width: { base: "minmax(0,1fr)", md: "minmax(0,1.1fr)" } },
  { key: "patient", header: "Patient", width: "minmax(0,1fr)", from: "md" },
  { key: "status", header: "Status", width: "6.5rem", from: "sm" },
  { key: "amount", header: "Amount", width: "6.75rem", align: "end" },
  { key: "ref", header: "Razorpay ID", width: "minmax(0,1.1fr)", from: "lg" },
];

const when = (iso: string) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

export function Transactions() {
  const [params, setParams] = useUrlParams();
  const preset = (PERIOD_PRESETS.some((p) => p.value === params.get("p")) ? params.get("p") : "this-month") as PeriodPreset;
  const range: DayRange = preset === "custom" ? { from: params.get("from") ?? "", to: params.get("to") ?? "" } : presetRange(preset);
  const type = (params.get("type") ?? "") as TransactionType;
  const statusParam = params.get("status") ?? "completed";
  const status = (statusParam === "any" ? "" : statusParam) as TransactionStatus;
  const q = params.get("q") ?? "";
  const sort = params.get("sort") === "amount" ? "amount" : "at";
  const descending = params.get("dir") !== "asc";
  const perPage = [25, 50, 100].includes(Number(params.get("per"))) ? Number(params.get("per")) : 25;
  const opened = params.get("order");
  const search = useSearchBox(q, (text) => setParams({ q: text }));
  const rangeInvalid = !!range.from && !!range.to && range.from > range.to;
  const [reportOpen, setReportOpen] = useState(false);

  const navigate = useNavigate();
  const orderHref = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("order", id);
    return `?${next.toString()}`;
  };

  const filters: TransactionQuery = useMemo(
    () => ({ type, status, q, date_from: range.from, date_to: range.to }),
    [type, status, q, range.from, range.to],
  );
  const query: TransactionQuery = useMemo(() => ({ ...filters, sort, descending, per_page: perPage }), [filters, sort, descending, perPage]);
  const list = useInfiniteQuery({
    queryKey: ["admin-transactions", query],
    queryFn: ({ pageParam }) => listTransactions({ ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled: !rangeInvalid,
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ["admin-transactions-summary", filters],
    queryFn: () => transactionsSummary(filters),
    enabled: !rangeInvalid,
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.data) ?? [], [list.data]);

  const narrowing = (preset !== "this-month" ? 1 : 0) + (type ? 1 : 0) + (statusParam !== "completed" ? 1 : 0);
  const activeFilters = narrowing + (q ? 1 : 0);
  const s = summary.data;

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Transactions</h2>
          <p className="text-sm text-gray-500">Payments and refunds, and the reports for GST and income-tax filing.</p>
        </div>
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          disabled={rangeInvalid}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90 disabled:opacity-50"
        >
          Download report
        </button>
      </div>

      <FilterBar
        label="Search and filter transactions"
        narrowing={narrowing}
        search={<SearchBox value={search.text} onChange={search.onChange} placeholder="Order no., patient, phone or Razorpay ID…" />}
      >
        <SelectFilter
          label="Period"
          value={preset}
          options={PERIOD_PRESETS}
          onChange={(p) =>
            setParams(p === "custom" ? { p, from: range.from, to: range.to } : { p: p === "this-month" ? null : p, from: null, to: null })
          }
          testId="transactions-filter-period"
        />
        {preset === "custom" && (
          <>
            <FilterField label="From">
              <input
                type="date"
                value={range.from}
                max={range.to || undefined}
                onChange={(e) => setParams({ from: e.target.value })}
                aria-invalid={rangeInvalid}
                className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
              />
            </FilterField>
            <FilterField label="To">
              <input
                type="date"
                value={range.to}
                min={range.from || undefined}
                onChange={(e) => setParams({ to: e.target.value })}
                aria-invalid={rangeInvalid}
                className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
              />
            </FilterField>
          </>
        )}
        <SelectFilter label="Type" value={type} options={TYPE_OPTIONS} onChange={(v) => setParams({ type: v })} testId="transactions-filter-type" />
        <SelectFilter
          label="Status"
          value={statusParam}
          options={STATUS_OPTIONS}
          onChange={(v) => setParams({ status: v === "completed" ? null : v })}
          testId="transactions-filter-status"
        />
        <SortFilter
          value={sort}
          options={SORTS}
          descending={descending}
          onChange={(v) => setParams({ sort: v === "at" ? null : v, dir: null })}
          onToggle={() => setParams({ dir: descending ? "asc" : null })}
        />
        <BatchSizeFilter value={perPage} onChange={(n) => setParams({ per: n === 25 ? null : String(n) })} />
        <ClearFilters
          count={activeFilters}
          onClear={() => {
            search.cancel();
            setParams({ q: null, p: null, from: null, to: null, type: null, status: null });
          }}
        />
      </FilterBar>
      {rangeInvalid && (
        <p role="alert" className="-mt-2 shrink-0 text-xs text-red-600">
          The “From” date is after the “To” date.
        </p>
      )}

      {/* One swipeable row on a phone, so the table keeps the screen; a grid from sm up. */}
      <div
        className={`-mx-1 flex shrink-0 snap-x gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-3 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4 ${
          summary.isPlaceholderData ? "opacity-60" : ""
        }`}
        aria-live="polite"
      >
        <Stat label="Received" value={s ? formatINR(s.payments_amount) : "—"} sub={s ? `${s.payments_count} payment${s.payments_count === 1 ? "" : "s"}` : ""} />
        <Stat label="Refunded" value={s ? formatINR(s.refunds_amount) : "—"} sub={s ? `${s.refunds_count} refund${s.refunds_count === 1 ? "" : "s"}` : ""} />
        <Stat label="Net" value={s ? formatINR(s.net_amount) : "—"} sub={rangeLabel(range)} />
        <Stat
          label="GST on orders paid"
          value={s ? formatINR(s.gst_total) : "—"}
          sub={
            s
              ? `${s.invoices_count} invoice${s.invoices_count === 1 ? "" : "s"} · taxable ${formatINR(s.taxable_value)} · CGST ${formatINR(s.cgst)} · SGST ${formatINR(s.sgst)} · IGST ${formatINR(s.igst)}`
              : ""
          }
        />
      </div>

      <VirtualTable
        label="Transactions"
        columns={COLUMNS}
        rows={rows}
        rowKey={(t) => `${t.kind}-${t.id}`}
        rowHeight={60}
        measureRows
        loading={list.isLoading}
        error={list.isError ? describeError(list.error) : null}
        onRetry={() => void list.refetch()}
        empty={activeFilters ? "No transactions match your filters." : "No payments or refunds this month."}
        total={list.data?.pages[0]?.total}
        hasMore={list.hasNextPage && !list.isPlaceholderData}
        loadingMore={list.isFetchingNextPage}
        loadMoreFailed={list.isFetchNextPageError}
        onLoadMore={list.fetchNextPage}
        resetKey={JSON.stringify(query)}
        renderRow={(t) => (
          <div
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest("a")) navigate(orderHref(t.order_uuid));
            }}
            className={`cursor-pointer transition-colors hover:bg-gray-50 ${list.isPlaceholderData ? "opacity-60" : ""}`}
          >
            <TransactionRow tx={t} href={orderHref(t.order_uuid)} />
          </div>
        )}
      />

      <Modal open={!!opened} title="Order" size="lg" onClose={() => setParams({ order: null })}>
        {opened && <AdminOrderDetail orderId={opened} />}
      </Modal>

      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} preset={preset} range={range} filters={filters} />
    </div>
  );
}

function TransactionRow({ tx: t, href }: { tx: Transaction; href: string }) {
  const refund = t.kind === "refund";
  const done = t.status === "succeeded" || t.status === "processed";
  const ref = t.rzp_refund_id ?? t.rzp_payment_id ?? t.rzp_order_id ?? "—";
  return (
    <TableRow
      columns={COLUMNS}
      className="min-h-15 py-2 text-sm"
      cells={{
        tx: (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${refund ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand"}`}>
                {refund ? "Refund" : "Payment"}
              </span>
              <Link to={href} className="truncate font-mono text-xs font-semibold text-gray-800 hover:text-brand hover:underline">
                {t.order_id}
              </Link>
            </div>
            <div className="mt-0.5 truncate text-xs text-gray-400">
              {when(t.at)}
              <span className="md:hidden"> · {t.patient_name ?? "—"}</span>
            </div>
            <div className="truncate text-xs text-gray-400 lg:hidden">{ref}</div>
          </div>
        ),
        patient: (
          <div className="min-w-0">
            <div className="truncate text-gray-800">{t.patient_name ?? "—"}</div>
            {t.patient_phone && <div className="truncate text-xs text-gray-400">{phoneLabel(t.patient_phone)}</div>}
          </div>
        ),
        status: <StatusPill status={t.status} />,
        amount: (
          <div>
            <div className={`tabular-nums ${refund ? "text-red-700" : done ? "text-gray-800" : "text-gray-400"}`}>
              {refund ? "−" : ""}
              {formatINR(t.amount)}
            </div>
            <div className="sm:hidden">
              <StatusPill status={t.status} />
            </div>
          </div>
        ),
        ref: (
          <div className="min-w-0">
            <div className="truncate font-mono text-xs text-gray-600">{ref}</div>
            {t.note && <div className="truncate text-xs text-gray-400" title={t.note}>{t.note}</div>}
          </div>
        ),
      }}
    />
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="w-40 shrink-0 snap-start rounded-xl border bg-white p-3 shadow-xs sm:w-auto sm:min-w-0 sm:p-4">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-0.5 truncate text-base font-semibold tabular-nums sm:text-lg">{value}</div>
      <div className="truncate text-xs text-gray-400" title={sub}>
        {sub}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- downloads

const FORMATS: { value: ReportFormat; label: string; detail: string }[] = [
  { value: "xlsx", label: "Excel", detail: "Transactions, plus Invoices, GST by rate, HSN summary and Documents sheets" },
  { value: "csv", label: "CSV", detail: "Every transaction with its GST, for any spreadsheet or accounting tool" },
  { value: "pdf", label: "PDF", detail: "A printable summary and register (up to 5,000 transactions)" },
  { value: "gstr1", label: "GSTR-1 JSON", detail: "For the GST offline tool: a calendar month or a quarter" },
  { value: "tally", label: "Tally XML", detail: "Sales and refund vouchers to import into TallyPrime" },
];

const TALLY_DEFAULTS: TallyLedgerNames = {
  company: "",
  party: "Razorpay",
  sales: "Sales @{rate}%",
  delivery: "Delivery Charges",
  cgst: "Output CGST",
  sgst: "Output SGST",
  igst: "Output IGST",
  refunds: "Sales Refunds",
};
const TALLY_KEY = "emx.tally.ledgers";

function savedLedgers(): TallyLedgerNames {
  try {
    return { ...TALLY_DEFAULTS, ...JSON.parse(localStorage.getItem(TALLY_KEY) ?? "{}") };
  } catch {
    return TALLY_DEFAULTS;
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "October 2026", or "Jul – Sep 2026" for a quarter. */
function filingLabel(range: DayRange, fp: string): string {
  const month = Number(fp.slice(0, 2));
  const year = fp.slice(2);
  const first = Number(range.from.slice(5, 7));
  return first === month
    ? new Date(Date.UTC(Number(year), month - 1, 1)).toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" })
    : `${MONTHS[first - 1]} – ${MONTHS[month - 1]} ${year}`;
}

function ReportDialog({
  open,
  onClose,
  preset: pagePreset,
  range: pageRange,
  filters,
}: {
  open: boolean;
  onClose: () => void;
  preset: PeriodPreset;
  range: DayRange;
  filters: TransactionQuery;
}) {
  return (
    <Modal open={open} title="Download report" size="lg" onClose={onClose}>
      {/* Mounted only while open: it starts from the page's period each time. */}
      <ReportForm onClose={onClose} pagePreset={pagePreset} pageRange={pageRange} filters={filters} />
    </Modal>
  );
}

function ReportForm({
  onClose,
  pagePreset,
  pageRange,
  filters,
}: {
  onClose: () => void;
  pagePreset: PeriodPreset;
  pageRange: DayRange;
  filters: TransactionQuery;
}) {
  const [format, setFormat] = useState<ReportFormat>("xlsx");
  const [preset, setPreset] = useState<PeriodPreset>(pagePreset);
  const [custom, setCustom] = useState<DayRange>(pageRange);
  const [ledgers, setLedgers] = useState<TallyLedgerNames>(savedLedgers);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = preset === "custom" ? custom : presetRange(preset);
  const rangeInvalid = !!range.from && !!range.to && range.from > range.to;
  const fp = gstr1Period(range);
  const returns = format === "gstr1" || format === "tally";
  const filterText = [
    filters.type === "payment" ? "payments only" : filters.type === "refund" ? "refunds only" : "",
    filters.status === "" ? "any status" : filters.status === "pending" ? "pending only" : filters.status === "failed" ? "failed or expired only" : "",
    filters.q?.trim() ? `matching “${filters.q.trim()}”` : "",
  ].filter(Boolean);
  const blocked = rangeInvalid || (format === "gstr1" && !fp);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      if (format === "tally") {
        try {
          localStorage.setItem(TALLY_KEY, JSON.stringify(ledgers));
        } catch {
          /* storage blocked: the names apply to this download only */
        }
      }
      const name = await downloadTransactionReport(
        format,
        returns ? { date_from: range.from, date_to: range.to } : { ...filters, date_from: range.from, date_to: range.to },
        format === "tally" ? ledgers : undefined,
      );
      toast.success(`Downloaded ${name}`);
      onClose();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 text-sm">
      <fieldset>
        <legend className="mb-2 font-medium text-gray-700">Format</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {FORMATS.map((f) => (
            <label
              key={f.value}
              className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${format === f.value ? "border-brand bg-brand-50" : "hover:bg-gray-50"}`}
            >
              <input type="radio" name="report-format" value={f.value} checked={format === f.value} onChange={() => setFormat(f.value)} className="mt-0.5 accent-brand" />
              <span>
                <span className="block font-medium text-gray-800">{f.label}</span>
                <span className="block text-xs text-gray-500">{f.detail}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 font-medium text-gray-700">Period</legend>
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Period">
            <Select value={preset} options={PERIOD_PRESETS} onChange={setPreset} data-testid="report-period" />
          </FilterField>
          {preset === "custom" && (
            <>
              <FilterField label="From">
                <input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="rounded-lg border px-2 py-1" />
              </FilterField>
              <FilterField label="To">
                <input type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="rounded-lg border px-2 py-1" />
              </FilterField>
            </>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">{rangeLabel(range)}</p>
        {rangeInvalid && <p className="text-xs text-red-600">The “From” date is after the “To” date.</p>}
      </fieldset>

      <Note>
        {returns ? (
          <>Every order paid in the period counts, whatever the page's filters. Refunds don't return GST, so they change no GST figure.</>
        ) : filterText.length ? (
          <>With the page's filters: {filterText.join(", ")}. The GST sheets cover every order paid in the period.</>
        ) : (
          <>All completed payments and refunds of the period. The GST sheets cover every order paid in it.</>
        )}
      </Note>

      {format === "gstr1" &&
        (fp ? (
          <Note>
            Files for <b>{filingLabel(range, fp)}</b> (period {fp}). Open it in the GST offline tool to check it before uploading; sales made outside
            this app aren't in it.
          </Note>
        ) : (
          <Note tone="warning">GSTR-1 is filed for a calendar month or a quarter: pick one above.</Note>
        ))}

      {format === "tally" && (
        <fieldset className="space-y-3">
          <legend className="font-medium text-gray-700">Tally ledgers</legend>
          <p className="text-xs text-gray-500">
            The vouchers post to these ledgers, which must already exist in Tally under exactly these names. “{"{rate}"}” in the sales ledger becomes
            the GST rate (“Sales @12%”). Saved in this browser for next time.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <LedgerField label="Company (blank: the one open in Tally)" value={ledgers.company} onChange={(v) => setLedgers({ ...ledgers, company: v })} />
            <LedgerField label="Payments received in" value={ledgers.party} onChange={(v) => setLedgers({ ...ledgers, party: v })} />
            <LedgerField label="Sales" value={ledgers.sales} onChange={(v) => setLedgers({ ...ledgers, sales: v })} />
            <LedgerField label="Delivery charges" value={ledgers.delivery} onChange={(v) => setLedgers({ ...ledgers, delivery: v })} />
            <LedgerField label="Output CGST" value={ledgers.cgst} onChange={(v) => setLedgers({ ...ledgers, cgst: v })} />
            <LedgerField label="Output SGST" value={ledgers.sgst} onChange={(v) => setLedgers({ ...ledgers, sgst: v })} />
            <LedgerField label="Output IGST" value={ledgers.igst} onChange={(v) => setLedgers({ ...ledgers, igst: v })} />
            <LedgerField label="Refunds" value={ledgers.refunds} onChange={(v) => setLedgers({ ...ledgers, refunds: v })} />
          </div>
          <button type="button" onClick={() => setLedgers(TALLY_DEFAULTS)} className="text-xs text-brand hover:underline">
            Reset to the defaults
          </button>
        </fieldset>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void download()}
          disabled={busy || blocked}
          data-testid="report-download"
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {busy ? "Preparing…" : `Download ${FORMATS.find((f) => f.value === format)?.label}`}
        </button>
        <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Note({ children, tone = "info" }: { children: ReactNode; tone?: "info" | "warning" }) {
  return <p className={`rounded-lg px-3 py-2 text-xs ${tone === "warning" ? "bg-amber-50 text-amber-900" : "bg-gray-50 text-gray-600"}`}>{children}</p>;
}

function LedgerField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      {label}
      <input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-lg border px-2 py-1.5 text-sm text-gray-800" />
    </label>
  );
}
